/* Admin dashboard page — analytics + user management.
 *
 * Designed to be replicable across other Lopettia products: the backend
 * (lopettia-auth) serves the same /api/admin/* endpoints to every app,
 * so this entire module can be lifted into La99 with only the import
 * paths changing. The Vite multi-page pattern handles the rest.
 *
 * Sections (tabs):
 *   - Overview:  KPI hero, MRR card, signup sparkline, tier/status bars,
 *                funnel, churn block
 *   - Users:     filterable + paginated users table with role actions
 *   - Grant:     30/60/90 day trial form (creates or extends a user)
 *   - Activity:  recent audit events
 *
 * Gate: requires logged-in user with role='admin'. The frontend gate is
 * cosmetic; the backend's require_admin dep is the real enforcement.
 */

import '../../styles/auth.css';
import '../../styles/admin.css';
import { bootShell, toast, describeError } from './_shell';
import { loadUser } from '../../auth';
import { escapeHtml, escapeAttr } from '../../modules/safe-dom';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T | null =>
  document.getElementById(id) as T | null;

// ============================================================================
// Types
// ============================================================================

interface AdminUserRow {
  id: string;
  email: string;
  display_name?: string | null;
  role: 'user' | 'admin';
  is_active: boolean;
  email_verified: boolean;
  subscription_tier: string;
  subscription_status: string;
  subscription_active_until?: string | null;
  stripe_customer_id?: string | null;
  last_login_at?: string | null;
  created_at: string;
}

interface UsersListResponse {
  users: AdminUserRow[];
  total: number;
  page: number;
  page_size: number;
}

interface Metrics {
  generated_at: string;
  window_days: number;
  trend_days: number;
  kpis: {
    total_users: number;
    paid_users: number;
    trial_users: number;
    past_due_users: number;
    mrr: number;
    arr: number;
    arpu: number;
    active_users_7d: number;
    active_users_30d: number;
    growth_pct: number | null;
    churn_rate_pct: number | null;
  };
  by_tier: Record<string, number>;
  by_status: Record<string, number>;
  signups: {
    today: number;
    last_7d: number;
    last_30d: number;
    last_90d: number;
    current_window: number;
    previous_window: number;
  };
  trends: { signups_daily: { date: string; count: number }[] };
  funnel: {
    registered: number;
    verified: number;
    trial_started: number;
    converted: number;
    verified_rate: number | null;
    trial_rate: number | null;
    conversion_rate: number | null;
  };
  churn: { canceled_in_window: number; baseline: number; rate_pct: number | null };
  engagement: { active_7d: number; active_30d: number };
  recent: {
    id: string;
    action: string;
    actor_email: string | null;
    severity: string;
    created_at: string | null;
    metadata: Record<string, unknown>;
  }[];
}

const state = {
  page: 1,
  pageSize: 50,
  q: '',
  statusFilter: '',
  tierFilter: '',
  roleFilter: '',
  grantDays: 30,
  total: 0,
  metrics: null as Metrics | null,
};

// ============================================================================
// Boot
// ============================================================================

async function init(): Promise<void> {
  bootShell();
  const user = await loadUser();
  if (!user) {
    location.href = '/login.html?next=/admin.html';
    return;
  }
  if (user.role !== 'admin') {
    $('admin-loading')!.style.display = 'none';
    $('admin-denied')!.style.display = '';
    return;
  }
  $('admin-loading')!.style.display = 'none';
  $('admin-content')!.style.display = '';
  wireTabs();
  wireControls();
  await Promise.all([loadMetrics(), loadUsers()]);
}

document.addEventListener('DOMContentLoaded', init);

// ============================================================================
// Tabs
// ============================================================================

function wireTabs(): void {
  document.querySelectorAll<HTMLButtonElement>('.admin-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.tab!;
      document.querySelectorAll('.admin-tab').forEach((b) => b.classList.remove('admin-tab-on'));
      btn.classList.add('admin-tab-on');
      document.querySelectorAll<HTMLElement>('.admin-tabpanel').forEach((p) => {
        p.hidden = p.dataset.panel !== target;
      });
    });
  });

  // Refresh button reloads both metrics + users without a full page reload.
  $('admin-refresh-btn')?.addEventListener('click', async () => {
    const btn = $('admin-refresh-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.classList.add('admin-refresh-spinning');
    try {
      await Promise.all([loadMetrics(), loadUsers()]);
      toast('Refreshed.');
    } catch (err: any) {
      toast(describeError(err));
    } finally {
      btn.disabled = false;
      btn.classList.remove('admin-refresh-spinning');
    }
  });
}

// ============================================================================
// API helper
// ============================================================================

async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers || {}) },
    ...init,
  });
  const text = await res.text();
  let body: any = null;
  if (text) { try { body = JSON.parse(text); } catch { body = text; } }
  if (!res.ok) {
    const message = (body && typeof body === 'object' && (body.detail?.message || body.detail)) ||
                    (typeof body === 'string' ? body : `HTTP ${res.status}`);
    const err = new Error(String(message)) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return body as T;
}

// ============================================================================
// Metrics + rendering
// ============================================================================

function fmtMoney(n: number): string {
  return '$' + Math.round(n).toLocaleString();
}

function fmtPct(n: number | null): string {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

function fmtRelative(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1)  return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}

async function loadMetrics(): Promise<void> {
  try {
    const m = await api<Metrics>('/api/admin/metrics');
    state.metrics = m;
    renderKpis(m);
    renderMrrBlock(m);
    renderSparkline(m);
    renderBars('admin-bars-tier',   m.by_tier,   ['free', 'pro', 'team']);
    renderBars('admin-bars-status', m.by_status, ['none', 'trial', 'active', 'past_due', 'canceled', 'incomplete']);
    renderFunnel(m);
    renderChurnBlock(m);
    renderActivity(m);
  } catch (err) {
    console.error('metrics load failed', err);
  }
}

function renderKpis(m: Metrics): void {
  const el = $('admin-kpis')!;
  const growth = m.kpis.growth_pct;
  const growthCls = growth == null ? 'admin-stat-muted' : (growth >= 0 ? 'admin-stat-trial' : 'admin-stat-bad');
  const churn = m.kpis.churn_rate_pct;
  const churnCls = churn != null && churn > 0 ? 'admin-stat-bad' : 'admin-stat-muted';
  // Disclaimers — explicit so we don't oversell heuristics as ground truth.
  const HEUR_MRR = 'Heuristic: assumes monthly billing for every active sub. Yearly subs counted as ~1/12 of true MRR. To fix: add a cycle column.';
  const HEUR_CHURN = 'Approximate: counts users whose status="canceled" was updated in the window. No subscription_events table yet → real cohort churn needs that.';
  // Headline 6: revenue · paying customers · pipeline · scale · momentum · retention.
  // Two rows of three. Secondary metrics (ARR, ARPU, Active 7d, Past due)
  // live in the MRR card grid + the by-status bars further down the page.
  el.innerHTML = `
    ${kpi('MRR',          fmtMoney(m.kpis.mrr),    'admin-stat-paid',  'monthly recurring',           HEUR_MRR)}
    ${kpi('Paid users',   m.kpis.paid_users,       'admin-stat-paid',  'active subscriptions')}
    ${kpi('Trial users',  m.kpis.trial_users,      'admin-stat-trial', 'currently trialing')}
    ${kpi('Total users',  m.kpis.total_users,      '',                 'all registrations')}
    ${kpi('Growth 30d',   fmtPct(growth),          growthCls,          'signups vs prev 30 days')}
    ${kpi('Churn 30d',    fmtPct(churn),           churnCls,           'monthly retention loss',      HEUR_CHURN)}
  `;
}

function kpi(label: string, value: string | number, tone: string, sub: string, heuristic?: string): string {
  const note = heuristic
    ? `<span class="admin-kpi-warn" title="${escapeAttr(heuristic)}" aria-label="${escapeAttr(heuristic)}">≈</span>`
    : '';
  return `
    <div class="admin-kpi ${tone}">
      <div class="admin-kpi-label">${label}${note}</div>
      <div class="admin-kpi-value">${value}</div>
      <div class="admin-kpi-sub">${sub}</div>
    </div>
  `;
}

function renderMrrBlock(m: Metrics): void {
  const el = $('admin-mrr-block')!;
  const days = m.window_days;
  el.innerHTML = `
    <div class="admin-mrr-current">
      <span class="admin-mrr-amount">${fmtMoney(m.kpis.mrr)}</span>
      <span class="admin-mrr-period">/ month</span>
    </div>
    <div class="admin-mrr-grid">
      <div><div class="admin-mrr-k">ARR</div><div class="admin-mrr-v">${fmtMoney(m.kpis.arr)}</div></div>
      <div><div class="admin-mrr-k">Paying</div><div class="admin-mrr-v">${m.kpis.paid_users}</div></div>
      <div><div class="admin-mrr-k">ARPU</div><div class="admin-mrr-v">${fmtMoney(m.kpis.arpu)}</div></div>
      <div><div class="admin-mrr-k">Window</div><div class="admin-mrr-v">${days}d</div></div>
    </div>
  `;
}

function renderSparkline(m: Metrics): void {
  const el = $('admin-spark-signups')!;
  const data = m.trends.signups_daily;
  if (!data.length) { el.innerHTML = '<p class="admin-section-sub">No data.</p>'; return; }
  const max = Math.max(1, ...data.map((d) => d.count));
  const W = 600, H = 160, PAD = 8;
  const stepX = (W - PAD * 2) / Math.max(1, data.length - 1);
  const pts = data.map((d, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - (d.count / max) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const linePath = `M ${pts.join(' L ')}`;
  const areaPath = `${linePath} L ${(W - PAD).toFixed(1)},${H - PAD} L ${PAD},${H - PAD} Z`;
  const total = data.reduce((a, b) => a + b.count, 0);

  el.innerHTML = `
    <div class="admin-spark-row">
      <div class="admin-spark-stat">
        <span class="admin-spark-num">${total}</span>
        <span class="admin-spark-lbl">signups · last ${m.trend_days}d</span>
      </div>
      <div class="admin-spark-mini">
        Today: <b>${m.signups.today}</b> · 7d: <b>${m.signups.last_7d}</b> · 30d: <b>${m.signups.last_30d}</b>
      </div>
    </div>
    <svg viewBox="0 0 ${W} ${H}" class="admin-spark-svg" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="sparkfade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stop-color="rgba(0,255,156,0.45)"/>
          <stop offset="100%" stop-color="rgba(0,255,156,0)"/>
        </linearGradient>
      </defs>
      <path d="${areaPath}" fill="url(#sparkfade)"/>
      <path d="${linePath}" fill="none" stroke="#00FF9C" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"/>
    </svg>
    <div class="admin-spark-axis">
      <span>${data[0]?.date.slice(5)}</span>
      <span>${data[data.length - 1]?.date.slice(5)}</span>
    </div>
  `;
}

function renderBars(id: string, data: Record<string, number>, order: string[]): void {
  const el = $(id)!;
  const total = order.reduce((a, k) => a + (data[k] || 0), 0) || 1;
  el.innerHTML = order.map((k) => {
    const v = data[k] || 0;
    const pct = (v / total) * 100;
    return `
      <div class="admin-bar-row">
        <div class="admin-bar-label">${escapeHtml(k)}</div>
        <div class="admin-bar-track">
          <div class="admin-bar-fill admin-bar-fill-${escapeAttr(k)}" style="width:${pct.toFixed(1)}%"></div>
        </div>
        <div class="admin-bar-value">${v}</div>
      </div>
    `;
  }).join('');
}

function renderFunnel(m: Metrics): void {
  const el = $('admin-funnel')!;
  const f = m.funnel;
  const steps = [
    { label: 'Registered',  value: f.registered,    rate: null,                  base: f.registered },
    { label: 'Verified',    value: f.verified,      rate: f.verified_rate,       base: f.registered },
    { label: 'Trial',       value: f.trial_started, rate: f.trial_rate,          base: f.verified },
    { label: 'Paid',        value: f.converted,     rate: f.conversion_rate,     base: f.trial_started },
  ];
  const max = Math.max(1, ...steps.map((s) => s.value));
  el.innerHTML = steps.map((s) => {
    const pct = (s.value / max) * 100;
    return `
      <div class="admin-funnel-row">
        <div class="admin-funnel-label">${s.label}</div>
        <div class="admin-funnel-track">
          <div class="admin-funnel-fill" style="width:${pct.toFixed(1)}%">${s.value}</div>
        </div>
        <div class="admin-funnel-rate">${s.rate == null ? '' : `${s.rate.toFixed(1)}%`}</div>
      </div>
    `;
  }).join('');
}

function renderChurnBlock(m: Metrics): void {
  const el = $('admin-churn-block')!;
  const c = m.churn;
  const rate = c.rate_pct;
  const tone = rate != null && rate > 5 ? 'admin-churn-bad' : 'admin-churn-ok';
  el.innerHTML = `
    <div class="admin-churn-main ${tone}">
      <span class="admin-churn-num">${rate == null ? '—' : `${rate.toFixed(1)}%`}</span>
      <span class="admin-churn-lbl">monthly</span>
    </div>
    <div class="admin-churn-grid">
      <div><div class="admin-mrr-k">Canceled</div><div class="admin-mrr-v">${c.canceled_in_window}</div></div>
      <div><div class="admin-mrr-k">Baseline</div><div class="admin-mrr-v">${c.baseline}</div></div>
    </div>
  `;
}

function renderActivity(m: Metrics): void {
  const el = $('admin-activity')!;
  if (!m.recent.length) {
    el.innerHTML = '<p class="admin-section-sub">No recent activity.</p>';
    return;
  }
  el.innerHTML = m.recent.map((r) => {
    const tone = r.severity === 'security' ? 'admin-activity-sec'
              : r.severity === 'error'    ? 'admin-activity-err'
              : '';
    const meta = Object.keys(r.metadata || {}).length
      ? `<code class="admin-activity-meta">${escapeHtml(JSON.stringify(r.metadata))}</code>`
      : '';
    return `
      <div class="admin-activity-row ${tone}">
        <div class="admin-activity-time">${fmtRelative(r.created_at)}</div>
        <div class="admin-activity-action">${escapeHtml(r.action)}</div>
        <div class="admin-activity-actor">${escapeHtml(r.actor_email || '—')}</div>
        <div class="admin-activity-meta-cell">${meta}</div>
      </div>
    `;
  }).join('');
}

// ============================================================================
// Users table
// ============================================================================

function planBadge(tier: string): string {
  const tone = tier === 'team' ? 'admin-pill-gold' :
               tier === 'pro'  ? 'admin-pill-matrix' :
               'admin-pill-muted';
  return `<span class="admin-pill ${tone}">${tier.toUpperCase()}</span>`;
}

function statusBadge(status: string): string {
  const tone =
    status === 'trial'    ? 'admin-pill-matrix' :
    status === 'active'   ? 'admin-pill-matrix' :
    status === 'past_due' ? 'admin-pill-coral' :
    status === 'canceled' ? 'admin-pill-muted' :
                            'admin-pill-muted';
  return `<span class="admin-pill ${tone}">${status}</span>`;
}

function rowHtml(u: AdminUserRow): string {
  const verified = u.email_verified ? '<span class="admin-check">✓</span>' : '<span class="admin-x">—</span>';
  const roleBadge = u.role === 'admin'
    ? '<span class="admin-pill admin-pill-coral">ADMIN</span>'
    : '<span class="admin-pill admin-pill-muted">USER</span>';
  return `
    <tr data-user-id="${u.id}" data-email="${escapeAttr(u.email)}" data-role="${u.role}">
      <td>
        <div class="admin-cell-email">
          ${escapeHtml(u.email)}
          ${u.display_name ? `<span class="admin-cell-name">${escapeHtml(u.display_name)}</span>` : ''}
        </div>
      </td>
      <td>${planBadge(u.subscription_tier)}</td>
      <td>${statusBadge(u.subscription_status)}</td>
      <td class="admin-cell-mono">${fmtDate(u.subscription_active_until)}</td>
      <td class="admin-cell-center">${verified}</td>
      <td>${roleBadge}</td>
      <td class="admin-cell-mono">${fmtDate(u.last_login_at)}</td>
      <td class="admin-cell-mono">${fmtDate(u.created_at)}</td>
      <td class="admin-cell-actions">
        ${u.role === 'admin'
          ? `<button class="admin-row-btn admin-row-btn-demote" data-action="demote">Demote</button>`
          : `<button class="admin-row-btn admin-row-btn-promote" data-action="promote">→ Admin</button>`}
      </td>
    </tr>
  `;
}

// escapeHtml / escapeAttr come from src/modules/safe-dom — F-17 consolidation.

async function loadUsers(): Promise<void> {
  const tbody = $('users-tbody')!;
  tbody.innerHTML = '<tr><td colspan="9" class="admin-table-empty">Loading…</td></tr>';
  try {
    const params = new URLSearchParams({
      page: String(state.page),
      page_size: String(state.pageSize),
    });
    if (state.q)            params.set('q', state.q);
    if (state.statusFilter) params.set('status', state.statusFilter);
    if (state.tierFilter)   params.set('tier', state.tierFilter);
    if (state.roleFilter)   params.set('role', state.roleFilter);
    const data = await api<UsersListResponse>(`/api/admin/users?${params.toString()}`);
    state.total = data.total;
    if (!data.users.length) {
      tbody.innerHTML = '<tr><td colspan="9" class="admin-table-empty">No users match these filters.</td></tr>';
    } else {
      tbody.innerHTML = data.users.map(rowHtml).join('');
      tbody.querySelectorAll<HTMLButtonElement>('.admin-row-btn').forEach((btn) => {
        btn.addEventListener('click', onRoleAction);
      });
    }
    renderPager(data);
  } catch (err: any) {
    tbody.innerHTML = `<tr><td colspan="9" class="admin-table-empty admin-table-error">${escapeHtml(describeError(err))}</td></tr>`;
  }
}

function renderPager(data: UsersListResponse): void {
  const pager = $('users-pager')!;
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  pager.innerHTML = `
    <span class="admin-pager-info">${data.total.toLocaleString()} users · page ${data.page} of ${totalPages}</span>
    <div class="admin-pager-nav">
      <button class="admin-row-btn" id="pager-prev" ${data.page <= 1 ? 'disabled' : ''}>← Prev</button>
      <button class="admin-row-btn" id="pager-next" ${data.page >= totalPages ? 'disabled' : ''}>Next →</button>
    </div>
  `;
  $('pager-prev')?.addEventListener('click', () => { if (state.page > 1) { state.page--; loadUsers(); } });
  $('pager-next')?.addEventListener('click', () => {
    const max = Math.ceil(state.total / state.pageSize);
    if (state.page < max) { state.page++; loadUsers(); }
  });
}

async function onRoleAction(e: Event): Promise<void> {
  const btn = e.currentTarget as HTMLButtonElement;
  const row = btn.closest('tr');
  if (!row) return;
  const userId = row.getAttribute('data-user-id');
  const email = row.getAttribute('data-email');
  const action = btn.getAttribute('data-action');
  if (!userId || !action) return;

  const newRole = action === 'promote' ? 'admin' : 'user';
  const verb = action === 'promote' ? 'Promote' : 'Demote';
  if (!confirm(`${verb} ${email} to ${newRole}?`)) return;

  btn.disabled = true;
  btn.textContent = '…';
  try {
    await api(`/api/admin/users/${encodeURIComponent(userId)}/role`, {
      method: 'POST',
      body: JSON.stringify({ role: newRole }),
    });
    toast(`${email}: role set to ${newRole}.`);
    await Promise.all([loadMetrics(), loadUsers()]);
  } catch (err: any) {
    toast(describeError(err));
    btn.disabled = false;
    btn.textContent = action === 'promote' ? '→ Admin' : 'Demote';
  }
}

// ============================================================================
// Grant trial form
// ============================================================================

async function onGrantSubmit(e: Event): Promise<void> {
  e.preventDefault();
  const email = ($('grant-email') as HTMLInputElement).value.trim().toLowerCase();
  const tier = ($('grant-tier') as HTMLSelectElement).value;
  const notes = ($('grant-notes') as HTMLInputElement).value.trim();
  const status = $('grant-status')!;
  const submit = $('grant-submit') as HTMLButtonElement;
  if (!email || !email.includes('@')) {
    status.textContent = 'Enter a valid email.';
    status.className = 'admin-form-status admin-form-status-err';
    return;
  }
  submit.disabled = true;
  status.textContent = 'Working…';
  status.className = 'admin-form-status';
  try {
    const res = await api<{ user_id: string; email: string; created: boolean; tier: string; active_until: string; invite_email_sent: boolean }>(
      '/api/admin/users/grant-trial',
      { method: 'POST', body: JSON.stringify({ email, days: state.grantDays, tier, notes: notes || undefined }) }
    );
    const verb = res.created ? 'Created' : 'Updated';
    const until = fmtDate(res.active_until);
    const invite = res.invite_email_sent ? ' Invite email sent.' : '';
    status.textContent = `${verb} ${res.email}: ${res.tier} trial until ${until}.${invite}`;
    status.className = 'admin-form-status admin-form-status-ok';
    ($('grant-email') as HTMLInputElement).value = '';
    ($('grant-notes') as HTMLInputElement).value = '';
    await Promise.all([loadMetrics(), loadUsers()]);
  } catch (err: any) {
    status.textContent = describeError(err);
    status.className = 'admin-form-status admin-form-status-err';
  } finally {
    submit.disabled = false;
  }
}

function wireControls(): void {
  // Grant form
  $('grant-form')?.addEventListener('submit', onGrantSubmit);
  document.querySelectorAll<HTMLButtonElement>('.admin-day-opt').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.admin-day-opt').forEach((b) => b.classList.remove('admin-day-opt-on'));
      btn.classList.add('admin-day-opt-on');
      state.grantDays = parseInt(btn.dataset.days || '30', 10);
    });
  });

  // Table filters (debounce search input)
  let searchTimer: number | null = null;
  ($('users-search') as HTMLInputElement | null)?.addEventListener('input', (ev) => {
    state.q = (ev.target as HTMLInputElement).value;
    state.page = 1;
    if (searchTimer) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(loadUsers, 280);
  });
  ['users-status-filter', 'users-tier-filter', 'users-role-filter'].forEach((id) => {
    const el = $(id) as HTMLSelectElement | null;
    el?.addEventListener('change', () => {
      if (id === 'users-status-filter') state.statusFilter = el.value;
      if (id === 'users-tier-filter')   state.tierFilter   = el.value;
      if (id === 'users-role-filter')   state.roleFilter   = el.value;
      state.page = 1;
      loadUsers();
    });
  });
}
